import { Mesh, Object3D, Vector3 } from "three";
import { GloryHole } from "../GloryHole";
import { clone } from "three/examples/jsm/utils/SkeletonUtils.js";
import { Event } from "event-system";
import { GameResourcesPackage } from "../../../../resources/GameResourcesPackage";
import { Ticker } from "pixi.js";
import { ISpawnable } from "../../spawner/ISpawnable";
import { GameViewModel } from "../../../../models/GameViewModel";
import { AudioController } from "../../../AudioController";
import { GameView3d } from "../../../../views/GameView3d";
import { PortalMole } from "../mole/PortalMole";
import { GameController } from "../../../../controllers/GameController";
import { Character } from "../../character/Character";
import { PortalText } from "./PortalText";
import { FontResourcesPackage } from "../../../../../../common/resources/FontResourcesPackage";
import { LocalizationModel } from "../../../../../../common/models/LocalizationModel";
import { BatchedRenderer } from "three.quarks";
import { ClickClodsParticle } from "../particles/ClickClodsParticle";
import { CancellationToken } from "../../../../../../../composition/CancellationToken";

export class Portal extends Object3D implements ISpawnable {
    public readonly onCanBeDestroyed: Event<ISpawnable>;
    public readonly cancellation: CancellationToken;
    private readonly hole: GloryHole;
    private textEffect: PortalText;

    public constructor(
        private readonly gameResources: GameResourcesPackage,
        private readonly fontResources: FontResourcesPackage,
        private readonly particlesRenderer: BatchedRenderer,
        private readonly localization: LocalizationModel,
        private readonly gameViewModel: GameViewModel,
        private readonly gameView3d: GameView3d,
        private readonly character: Character,
        private readonly audioController: AudioController,
        private readonly gameController: GameController,
        private releaseMolesTime: number,
        private hitsToDestroy: number,
        private molesCount: number,
        public readonly fieldIndex: number
    ) {
        super();
        this.onCanBeDestroyed = new Event();
        this.cancellation = new CancellationToken();
        this.cancellation.onCanceled.add(this.cancel, this);
        this.hole = new GloryHole(clone(gameResources.assets_game_Hole_glb.scene.children[0]) as Mesh, 2);
        this.add(this.hole);
        this.addEventListener("added", this.onAddedToStage);
    }

    public async hit(): Promise<void> {
        if (this.hitsToDestroy == 0) {
            return;
        }
        const particle = new ClickClodsParticle(this.gameResources, this.particlesRenderer);
        particle.position.set(0, 1.25, 0);
        this.add(particle);
        particle.play();
        if (--this.hitsToDestroy == 0) {
            Ticker.shared.remove(this.onTick, this);
            this.textEffect.destroy();
            await this.hole.close(500);
            this.onCanBeDestroyed.emit(this);
        }
    }

    private async onAddedToStage(): Promise<void> {
        await this.hole.open(500);

        if (this.cancellation.isCancellationRequested) {
            return;
        }

        this.textEffect = new PortalText(this.fontResources, this.localization);
        this.add(this.textEffect);
        Ticker.shared.add(this.onTick, this);
    }

    private async onTick(): Promise<void> {
        this.releaseMolesTime = Math.max(this.releaseMolesTime - Ticker.shared.deltaMS, 0);
        if (this.releaseMolesTime == 0) {
            Ticker.shared.remove(this.onTick, this);
            this.textEffect.destroy();
            this.spawnMoles();
            await this.hole.close(500);
            this.onCanBeDestroyed.emit(this);
        }
    }

    private spawnMoles(): void {
        for (let i = 0; i < this.molesCount; i++) {
            const freeSpawnIndices = this.gameViewModel.getFreeSpawnIndices().shuffle();
            if (freeSpawnIndices.length === 0) {
                break; // no place left
            }

            const fieldIndex = freeSpawnIndices[0]; // take first after shuffle

            const spawnPoint = this.getWorldPosition(new Vector3());
            const mole = new PortalMole(this.gameResources, this.gameView3d, this.particlesRenderer, this.character, this.audioController, this.gameController, fieldIndex);
            this.gameView3d.worldToLocal(spawnPoint);
            this.gameView3d.addSpawnableItem(mole);

            const releasePosition = mole.parent.worldToLocal(this.gameViewModel.indexToGameCoords(fieldIndex));
            const direction = new Vector3().subVectors(this.position, releasePosition);
            const angle = Math.atan2(direction.x, direction.z);
            mole.sendFlying(spawnPoint, releasePosition, angle);
        }
    }

    private async cancel(): Promise<void> {
        Ticker.shared.remove(this.onTick, this);
        await this.hole.close(500);
        this.onCanBeDestroyed.emit(this);
    }

    public destroy(): void {
        Ticker.shared.remove(this.onTick, this);
        this.removeEventListener("added", this.onAddedToStage);
        this.cancellation.onCanceled.remove(this.cancel, this);
        this.textEffect?.destroy();
        this.hole?.destroy();
    }

}
